/**
 * Look-dev (spec 05 §12): one level as a diorama with Bub and Bob on it, every look knob in
 * the URL, and `window.__look` for exact frame captures. This is where the look is judged
 * before any game rules exist.
 *
 *   ?scene=lookdev&level=1&ppt=14&pitch=18&lock=0.85&outlines=1&crt=0&t=0.3
 *   &props=0 no stand-in bubbles (for gallery shots of a level as it is); &hud=0 no side panels
 *   &fx=-12&fy=-10&ppt=60 close-up on Bub; &pose=idle|walk|blow
 *   &c64=1   the 1987 look: the frame locked to the sixteen C64 colours, full-strength dither
 */

import { Mesh, Scene, SphereGeometry, Vector3 } from 'three';
import stageGrade from '../../content/grades/stage.json';
import { gridFromRows } from '../levels/grid';
import { LEVELS, level } from '../levels';
import { bolt, flame, letterVoxels, waterDrop } from '../render/actors/bubbleContents';
import { buildDragon } from '../render/actors/dragon';
import { buildMonster, face } from '../render/actors/monsters';
import { StageCamera } from '../render/camera/StageCamera';
import { MeshBuilder } from '../render/kit/MeshBuilder';
import { createGlassBubbleMaterial } from '../render/materials/glassBubble';
import { createGradientMap, createToonMaterial, OUTLINE, sharedUniforms } from '../render/materials/toon';
import { levelLook } from '../render/levelLook';
import c64 from '../../content/palettes/c64.json';
import { hexToRgb8 } from '../render/color';
import { buildLut, type Grade, Palette } from '../render/palette';
import { RenderPipeline } from '../render/pipeline/RenderPipeline';
import { tileOrigin } from '../render/stage/blocks';
import { createStageLights, StageView } from '../render/stage/StageView';
import { createHudCanvas, drawHud, type HudColours } from '../ui/hud/panels';
import { chooseSize, createRenderer, exposeLook, num } from './devKit';
import { poseDragon } from './lookdevPoses';

export function startLookdev(canvas: HTMLCanvasElement, params: URLSearchParams): void {
  const def = level(Math.round(num(params, 'level', 1)));
  const c64Look = params.get('c64') === '1';
  // The 1987 look: the C64's background was black, so the cave wall is black (the glass bubbles
  // are drawn over whatever is behind them, so this is done in the palette, not with fog).
  const look = c64Look
    ? { ...levelLook(def), backdrop: ['#000000', '#000000', '#000000'], fog: '#000000' }
    : levelLook(def);
  const grade = stageGrade as Grade;
  const palette = new Palette(look, params.get('figures') === 'c64' ? 'c64' : 'arcade');
  const gradientMap = createGradientMap();

  const renderer = createRenderer(canvas);

  const grid = gridFromRows(def.tiles);
  const scene = new Scene();
  const stage = new StageView({ grid, style: look.style, palette, gradientMap, seed: 1 });
  scene.add(stage.group, createStageLights(grade, grid.width, grid.height));

  const actorMat = createToonMaterial({ gradientMap, outline: OUTLINE.player, depthBias: 3 });
  const bub = buildDragon(palette, 'bub', actorMat);
  const bob = buildDragon(palette, 'bob', actorMat);
  /** Feet of a 24x21 C64 sprite whose top-left is (x, y) in playfield pixels. */
  const feet = (x: number, y: number): Vector3 =>
    new Vector3((x + 12) / 8 - grid.width / 2, grid.height / 2 - (y + 21) / 8, 0);
  const { p1, p2 } = LEVELS.playerSpawns;
  bub.root.position.copy(feet(p1.x, p1.y));
  bob.root.position.copy(feet(p2.x, p2.y));
  scene.add(bub.root, bob.root);

  // Bubbles: clear glass in the bubble colour (every C64 bubble was the same green; the arcade's
  // were green or blue by who blew them - `bob` shows the second).
  const sphere = new SphereGeometry(0.9, 40, 28);
  const glass = (ramp: string) => createGlassBubbleMaterial({
    tint: palette.color(`${ramp}.1`), rim: palette.color(`${ramp}.2`),
  });
  const ages = [glass('bubble'), glass('bob'), glass('bubble'), glass('bubble')];
  // Inside a bubble a figure sits behind the bubble's front surface (glassBubble.ts).
  const trappedMat = createToonMaterial({ gradientMap, outline: OUTLINE.character, depthBias: 1.5 });
  const bubbleAt = (col: number, row: number, age: number): Vector3 => {
    const o = tileOrigin(grid, col, row);
    const bubble = new Mesh(sphere, ages[age]);
    bubble.position.set(o.x + 1, o.y + 1, 0);
    scene.add(bubble);
    return bubble.position;
  };
  const inside = (mb: MeshBuilder, at: Vector3): void => {
    const m = new Mesh(mb.build(), trappedMat);
    m.position.copy(at);
    scene.add(m);
  };
  // Stand-in bubbles placed for level 1's open spaces: a trapped Zen-chan, the four ages, and the specials.
  if (params.get('props') !== '0') {
    const trapped = buildMonster('zenChan', false, palette, trappedMat);
    trapped.root.position.copy(bubbleAt(7, 20, 0)).add(new Vector3(0, -0.72, 0));
    trapped.root.scale.setScalar(0.8);
    face(trapped, 1);
    scene.add(trapped.root);
    // Bub mid-blow: a shot-phase bubble, still small, leaving his mouth (spec 02 §4: small -> full).
    const shot = new Mesh(sphere, ages[0]);
    shot.position.copy(bub.root.position).add(new Vector3(1.5, 0.95, 0));
    shot.scale.setScalar(0.55);
    scene.add(shot);
    bubbleAt(10, 21, 0);
    bubbleAt(21, 20, 1);
    bubbleAt(15, 5, 2);
    bubbleAt(17, 6, 3);
    inside(letterVoxels(palette, 'E'), bubbleAt(9, 3, 1));
    inside(waterDrop(palette), bubbleAt(12, 3, 1));
    inside(flame(palette), bubbleAt(20, 3, 1));
    inside(bolt(palette), bubbleAt(23, 3, 1));
  }

  const size = chooseSize(params);
  const camera = new StageCamera(size, num(params, 'ppt', 14) * size.scale, num(params, 'pitch', 18));
  camera.focus.set(num(params, 'fx', 0), num(params, 'fy', 0), 0);
  camera.update();

  const pipeline = new RenderPipeline(renderer, palette.buildLut(), palette.color('ink.0'), size);
  pipeline.setGrade(grade, look.fog);
  pipeline.settings.paletteLock = num(params, 'lock', pipeline.settings.paletteLock);
  pipeline.settings.ditherAmp = num(params, 'dither', pipeline.settings.ditherAmp);
  pipeline.settings.outlines = params.get('outlines') !== '0';
  pipeline.settings.crt = params.get('crt') === '1';
  if (c64Look) {
    pipeline.setLut(buildLut(c64.colours.map(hexToRgb8)));
    pipeline.settings.paletteLock = 1;
    pipeline.settings.ditherAmp = 0.16;
  }

  const hud = createHudCanvas(document.body);
  const hudCtx = hud.canvas.getContext('2d');
  const hudColours: HudColours = {
    p1: palette.hex('bub.3'), p2: palette.hex('bob.3'), text: palette.hex('paper.0'),
    dim: palette.hex('metal.1'), accent: palette.hex('hudAccent.0'),
  };
  const drawPanels = (): void => {
    if (!hudCtx || params.get('hud') === '0') return;
    hud.place(pipeline.viewRect);
    drawHud(hudCtx, {
      level: def.n,
      hiScore: 30000,
      players: [
        { playing: true, score: 12340, lives: 3, extend: [true, false, true, false, false, false] },
        { playing: true, score: 5600, lives: 2, extend: [false, false, false, false, true, false] },
      ],
    }, hudColours);
  };

  const frozen = params.has('t');
  const start = performance.now();
  const frame = (): void => {
    const t = frozen ? num(params, 't', 0) : (performance.now() - start) / 1000;
    sharedUniforms.uTime.value = t;
    const pose = params.get('pose');
    poseDragon(bub, pose === 'idle' || pose === 'walk' || pose === 'blow' ? pose : params.get('props') === '0' ? 'walk' : 'blow', t);
    poseDragon(bob, 'idle', t, -1);
    pipeline.render(scene, camera);
    drawPanels();
    if (!frozen) requestAnimationFrame(frame);
  };
  frame();

  exposeLook(pipeline, frame, { camera }, hud.canvas);
}
